import {
  PGA_COACH_ACTION_BLUE,
  PGA_COACH_GROUP_EVENT_COLOR,
  PGA_COACH_PRIVATE_LESSON_COLOR,
} from 'pages/pga-coach/dashboard/dashboard-colors'
import {
  buildMixSegments,
  fitToMonths,
  formatCount,
  formatCurrency,
  formatFullTimeLabel,
  formatMonthLabel,
  formatOptionalDecimal,
  formatOptionalPercent,
  formatPercent,
  getMixInsight,
  getRevenueInsight,
  hasTakeHome,
  sumMonthlyCounts,
} from './utils'

const toChart = ({ data, months, dataKey, emptyMessage, ...chart }) => ({
  ...chart,
  emptyMessage,
  isEmpty: !data?.some(point => months.includes(point.month)),
  points: fitToMonths(data, months, dataKey).map(point => ({
    label: `${formatMonthLabel(point.month, 'LLL')} '${formatMonthLabel(
      point.month,
      'yy',
    )}`,
    value: point[dataKey],
  })),
})

// Shapes the analytics the page shows into plain sections for the PDF, so the
// report always matches what is on screen for the same window.
const toMix = ({ title, mix, formatValue, insight, emptyMessage }) => ({
  title,
  stats: mix.total
    ? mix.segments.map(segment => ({
      label: `${segment.noun} (${segment.percent}%)`,
      value: formatValue(segment.value),
    }))
    : [],
  note: insight || emptyMessage,
})

export const getReportData = ({
  analytics,
  benchmarks,
  benchmarksError,
  months,
  topLessonTypes,
  topGroupEvents,
}) => {
  const isTakeHome = hasTakeHome(benchmarks)
  const format = (formatter, value) =>
    benchmarksError ? 'Unable to load' : formatter(value)
  const privateBookings = sumMonthlyCounts(
    analytics?.privateLessonTypes?.byMonth,
  )
  const groupBookings = sumMonthlyCounts(analytics?.groupEvents?.byMonth)
  const bookingMix = buildMixSegments({
    private: privateBookings,
    group: groupBookings,
  })
  const revenueMix = buildMixSegments({
    private: analytics?.privateLessonTypes?.revenue,
    group: analytics?.groupEvents?.revenue,
  })

  return [
    {
      title: 'Revenue',
      stats: [
        {
          label: isTakeHome
            ? 'Take-home through PGA Coach'
            : 'Earnings through PGA Coach',
          value: formatCurrency(analytics?.earnings?.total),
        },
        {
          label: 'Private lesson revenue',
          value: formatCurrency(analytics?.privateLessonTypes?.revenue),
        },
        {
          label: 'Group event revenue',
          value: formatCurrency(analytics?.groupEvents?.revenue),
        },
      ],
      charts: [
        toChart({
          title: isTakeHome ? 'Take-home by month' : 'Payments by month',
          color: PGA_COACH_ACTION_BLUE,
          data: analytics?.earnings?.byMonth,
          months,
          dataKey: 'total',
          emptyMessage: isTakeHome
            ? 'No take-home recorded.'
            : 'No earnings recorded.',
          formatValue: formatCurrency,
        }),
      ],
    },
    ...(isTakeHome || benchmarksError
      ? [
        {
          title: 'Performance benchmarks',
          stats: [
            {
              label: 'Hours coached',
              value: format(formatOptionalDecimal, benchmarks?.billableHours),
            },
            {
              label: 'Utilization',
              value: format(
                formatOptionalPercent,
                benchmarks?.utilizationPercent,
              ),
            },
            {
              label: formatFullTimeLabel(benchmarks),
              value: format(
                formatOptionalPercent,
                benchmarks?.fullTimeProgressPercent,
              ),
            },
          ],
        },
      ]
      : []),
    {
      title: 'Leads',
      stats: [
        {
          label: 'Total leads',
          value: (analytics?.leads?.totalCount || 0).toLocaleString(),
        },
        {
          label: 'Unviewed leads',
          value: (analytics?.leads?.unviewedCount || 0).toLocaleString(),
        },
        {
          label: 'Conversion rate',
          value: formatPercent(analytics?.leads?.conversionRate),
        },
      ],
      charts: [
        toChart({
          title: 'Leads acquired by month',
          color: PGA_COACH_ACTION_BLUE,
          data: analytics?.leads?.byMonth,
          months,
          dataKey: 'count',
          emptyMessage: 'No leads.',
          formatValue: value => value.toLocaleString(),
        }),
      ],
    },
    {
      title: 'Booking mix',
      mixes: [
        toMix({
          title: 'By bookings',
          mix: bookingMix,
          formatValue: value => formatCount(value, 'booking'),
          insight: getMixInsight(bookingMix),
          emptyMessage: 'No bookings.',
        }),
        toMix({
          title: 'By revenue',
          mix: revenueMix,
          formatValue: formatCurrency,
          insight: getRevenueInsight(revenueMix),
          emptyMessage: 'No revenue.',
        }),
      ],
    },
    {
      title: 'Private lessons',
      charts: [
        toChart({
          title: 'Private lessons by month',
          color: PGA_COACH_PRIVATE_LESSON_COLOR,
          data: analytics?.privateLessonTypes?.byMonth,
          months,
          dataKey: 'count',
          emptyMessage: 'No private lessons.',
          formatValue: value => value.toLocaleString(),
        }),
      ],
      lists: [
        {
          title: 'Top booked lesson types',
          rows: topLessonTypes,
          emptyMessage: 'No lesson bookings.',
        },
      ],
    },
    {
      title: 'Group events',
      charts: [
        toChart({
          title: 'Enrollments by month',
          color: PGA_COACH_GROUP_EVENT_COLOR,
          data: analytics?.groupEvents?.byMonth,
          months,
          dataKey: 'count',
          emptyMessage: 'No group event enrollments.',
          formatValue: value => value.toLocaleString(),
        }),
      ],
      lists: [
        {
          title: 'Top group events',
          rows: topGroupEvents,
          emptyMessage: 'No group event registrations.',
        },
      ],
    },
  ]
}
